import { AlertTriangle, Search, UserCog, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  useAdminContent,
  useAdminUsers,
  useCreateAdminUser,
} from "../../api/admin";
import { AdminUserCreateParams, AdminUsersFilters } from "../../types/admin";
import { useImpersonate } from "../../hooks/useImpersonate";
import { formatDate } from "../../utils/dateUtils";
import { DEFAULT_PAGE } from "./pagination";
import { apiErrorMessage } from "./notify";
import {
  Badge,
  BOOK_STATUS_LABELS,
  BookStatusBadge,
  Button,
  Card,
  Field,
  NumberField,
  PageHeader,
  PaginationBar,
  ShopifyOrderWarning,
  SelectField,
  SubscriptionBadge,
} from "./ui";
import { SEARCH_INPUT_PROPS } from "./inputs";

export const AdminUsers = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filters, setFiltersState] = useState<AdminUsersFilters>({});
  const [page, setPage] = useState(DEFAULT_PAGE);
  const { impersonate, loading: impersonating, currentUser } = useImpersonate();

  // Any filter change goes back to the first page
  const setFilters = (update: (f: AdminUsersFilters) => AdminUsersFilters) => {
    setFiltersState(update);
    setPage((p) => ({ ...p, page: 1 }));
  };

  const query = useMemo<AdminUsersFilters>(
    () => ({ ...filters, ...page, search: search.trim() || undefined }),
    [filters, page, search],
  );

  const { data, isLoading } = useAdminUsers(query);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Utilisateurs"
        subtitle={data ? `${data.pagination.total} utilisateur(s)` : undefined}
        action={
          <Button variant="primary" onClick={() => setCreating((c) => !c)}>
            <UserPlus className="w-4 h-4" /> Créer un utilisateur
          </Button>
        }
      />

      {creating && <CreateUserCard onDone={() => setCreating(false)} />}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage((p) => ({ ...p, page: 1 }));
            }}
            {...SEARCH_INPUT_PROPS}
            placeholder="Rechercher par nom ou email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <select
          value={filters.subscription_status ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              subscription_status: e.target.value || undefined,
            }))
          }
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Tous les abonnements</option>
          <option value="active">Abonné</option>
          <option value="pending">En attente</option>
          <option value="cancelled">Annulé</option>
        </select>

        <select
          value={filters.book_status ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              book_status: e.target.value || undefined,
            }))
          }
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Tous les livres</option>
          <option value="none">Sans livre</option>
          {Object.entries(BOOK_STATUS_LABELS).map(([status, label]) => (
            <option key={status} value={status}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={filters.subscription_product ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              subscription_product: e.target.value || undefined,
            }))
          }
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Tous les produits</option>
          <option value="print">Print</option>
          <option value="pdf">PDF</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={filters.admins_only ?? false}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                admins_only: e.target.checked || undefined,
              }))
            }
            className="rounded border-gray-300"
          />
          Admins
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={filters.multiple_subscriptions ?? false}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                multiple_subscriptions: e.target.checked || undefined,
              }))
            }
            className="rounded border-gray-300"
          />
          Plusieurs abonnements
        </label>
      </div>

      <Card>
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Chargement…</div>
        ) : data && data.users.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                <th className="px-5 py-3 font-medium">Utilisateur</th>
                <th className="px-5 py-3 font-medium">Abonnement</th>
                <th className="px-5 py-3 font-medium">Livre</th>
                <th className="px-5 py-3 font-medium">Inscrit le</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.users.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {user.full_name || "—"}
                      </span>
                      {user.is_admin && <Badge color="red">Admin</Badge>}
                      {!user.is_confirmed && (
                        <Badge color="yellow">Non confirmé</Badge>
                      )}
                      {user.is_locked && <Badge color="orange">Bloqué</Badge>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {user.email} · ID {user.id}
                    </p>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <SubscriptionBadge status={user.subscription?.status} />
                      {user.subscription?.product && (
                        <Badge color="blue">
                          {user.subscription.product === "print"
                            ? "Print"
                            : "PDF"}
                        </Badge>
                      )}
                      {user.subscription?.with_audiobook && (
                        <Badge color="purple">Audio</Badge>
                      )}
                      {user.subscriptions_count > 1 && (
                        <Badge color="red">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {user.subscriptions_count} abonnements
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <BookStatusBadge status={user.book_status} />
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {/* The row opens the user: keep the click on the button */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        onClick={() => impersonate(user.id)}
                        disabled={impersonating || user.id === currentUser?.id}
                      >
                        <UserCog className="w-3.5 h-3.5" /> Se connecter en tant
                        que
                      </Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-gray-400">
            Aucun utilisateur trouvé
          </div>
        )}
        <PaginationBar pagination={data?.pagination} onChange={setPage} />
      </Card>
    </>
  );
};

// CREATE =====================================================================
const EMPTY_USER: AdminUserCreateParams = {
  user: { email: "", first_name: "", last_name: "" },
  product: "pdf",
  with_audiobook: false,
  quantity: 1,
  send_invitation: true,
};

/** Same outcome as a Shopify order: invited user + active subscription. */
const CreateUserCard = ({ onDone }: { onDone: () => void }) => {
  const navigate = useNavigate();
  const create = useCreateAdminUser();
  const { data: languages } = useAdminContent("languages");
  const [form, setForm] = useState<AdminUserCreateParams>(EMPTY_USER);

  const setUser =
    (key: keyof AdminUserCreateParams["user"]) => (value: string) =>
      setForm((f) => ({ ...f, user: { ...f.user, [key]: value } }));

  const submit = () =>
    create.mutate(form, {
      onError: (error) => {
        toast.error(apiErrorMessage(error));
      },
      onSuccess: (detail) => {
        toast.success("Utilisateur créé");
        onDone();
        navigate(`/admin/users/${detail.user.id}`);
      },
    });

  return (
    <Card title="Nouvel utilisateur" className="mb-6">
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Email"
            type="email"
            value={form.user.email}
            onChange={setUser("email")}
          />
          <Field
            label="Prénom"
            value={form.user.first_name ?? ""}
            onChange={setUser("first_name")}
          />
          <Field
            label="Nom"
            value={form.user.last_name ?? ""}
            onChange={setUser("last_name")}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <SelectField
            label="Produit"
            value={form.product}
            onChange={(v) =>
              setForm((f) => ({ ...f, product: v as "pdf" | "print" }))
            }
            options={[
              ["pdf", "PDF"],
              ["print", "Print"],
            ]}
          />
          <NumberField
            label="Livres achetés"
            value={form.quantity}
            onChange={(v) => setForm((f) => ({ ...f, quantity: v }))}
          />
          <SelectField
            label="Langue"
            value={String(form.user.language_id ?? "")}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                user: { ...f.user, language_id: v ? Number(v) : undefined },
              }))
            }
            options={[
              ["", "Par défaut"],
              ...(languages?.records ?? []).map((l): [string, string] => [
                String(l.id),
                String(l.name),
              ]),
            ]}
          />
        </div>
        <div className="flex gap-6 text-sm text-gray-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.with_audiobook}
              onChange={(e) =>
                setForm((f) => ({ ...f, with_audiobook: e.target.checked }))
              }
              className="rounded border-gray-300"
            />
            Audiobook inclus
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.send_invitation}
              onChange={(e) =>
                setForm((f) => ({ ...f, send_invitation: e.target.checked }))
              }
              className="rounded border-gray-300"
            />
            Envoyer l'invitation maintenant
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field
              label="Commande Shopify (lien de la commande ou son ID)"
              value={form.shopify_order ?? ""}
              onChange={(v) => setForm((f) => ({ ...f, shopify_order: v }))}
            />
          </div>
          <Field
            label="N° de commande (ex. #1042)"
            value={form.shopify_order_name ?? ""}
            onChange={(v) => setForm((f) => ({ ...f, shopify_order_name: v }))}
          />
        </div>
        <ShopifyOrderWarning />
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={submit}
            disabled={create.isLoading || !form.user.email}
          >
            Créer
          </Button>
          <Button onClick={onDone}>Annuler</Button>
        </div>
      </div>
    </Card>
  );
};
